import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TextField from "@material-ui/core/TextField";
import StandardModal from "js/components/shared/StandardModal";
import Radio from "js/components/Radio";
import { InfoBox } from "../shared/InfoBox";
import { PrimaryButton } from "../shared/button/PrimaryButton";
import Checkbox from "../shared/Checkbox";
import { default as IconPinClass } from "assets/IconPinClass.svg";

export type ClassroomCreateSubmitResponse = { error?: string };

export interface ClassroomCreateProps {
	open: boolean;
	heading: string;
	onSubmit: (name: string, isMonitoringClass: boolean, isPinned: boolean) => Promise<ClassroomCreateSubmitResponse>;
	onClose: () => void;
	isMonitoringAuthorised?: boolean;
	bodyClass?: string;
}

const MAX_CLASS_NAME_LENGTH = 50;

export function ClassroomCreate({
	open,
	heading,
	onSubmit,
	onClose,
	isMonitoringAuthorised,
	bodyClass,
}: ClassroomCreateProps): JSX.Element {
	const { t } = useTranslation();
	const [classroomName, setClassroomName] = useState("");
	const [classroomNameUniqueError, setClassroomNameUniqueError] = useState("");
	const [classroomNameLengthError, setClassroomNameLengthError] = useState("");
	const [monitorModeChecked, setMonitorModeChecked] = useState(false);
	const [pinned, setPinned] = useState(false);
	const previousOpenRef = useRef(open);

	const trimmedClassroomName = classroomName.trim();
	const isNameEmpty = trimmedClassroomName.length === 0;
	const isNameTooLong = trimmedClassroomName.length > MAX_CLASS_NAME_LENGTH;
	const isSaveDisabled = isNameEmpty || isNameTooLong;
	const helperText = classroomNameLengthError || classroomNameUniqueError;
	const hasError = !!helperText;

	const reset = () => {
		setClassroomName("");
		setClassroomNameUniqueError("");
		setClassroomNameLengthError("");
		setMonitorModeChecked(false);
	};

	useEffect(() => {
		if (previousOpenRef.current !== open) {
			reset();
			previousOpenRef.current = open;
		}
	}, [open]);

	const handleClose = () => {
		onClose();
	};

	const handleSubmit = async () => {
		if (isNameEmpty || isNameTooLong) {
			return;
		}

		setClassroomNameUniqueError("");

		const response = await onSubmit(trimmedClassroomName, monitorModeChecked, pinned);

		if (!response.error) {
			handleClose();
			reset();
		} else {
			setClassroomNameUniqueError(response.error);
		}
	};

	const handleClassroomNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const nextName = event.target.value;
		setClassroomName(nextName);
		setClassroomNameUniqueError("");

		if (nextName.trim().length > MAX_CLASS_NAME_LENGTH) {
			setClassroomNameLengthError(t("overview.classNameMaxCharsError"));
		} else {
			setClassroomNameLengthError("");
		}
	};

	const handleModeChange = (value: string) => {
		setMonitorModeChecked(value === "monitor");
	};

	const actions = (
		<div>
			<PrimaryButton className="mui-flat-button" label={t("overview.save")} onClick={handleSubmit} disabled={isSaveDisabled} />
		</div>
	);

	return (
		<StandardModal size="medium" title={heading} actions={actions} open={open} close={handleClose} bodyClass={bodyClass}>
			<div className="ClassroomCreate_infoContent">
				<label className="ClassroomCreate_classNameLabel" htmlFor="classroomName">
					{t("overview.className")}
				</label>
				<div className="ClassroomCreate_Pin_Container_Text_Secondary">{t("overview.classNameHint")}</div>
				<TextField
					id="classroomName"
					className="ClassroomCreate_muiInput"
					data-cy="class_room-input"
					placeholder={t("overview.enterClassName")}
					value={classroomName}
					onChange={handleClassroomNameChange}
					error={hasError}
					helperText={helperText}
					fullWidth
					autoFocus
				/>
			</div>
			<br />
			{isMonitoringAuthorised ? (
				<div>
					<div
						style={{
							display: "flex",
							flexDirection: "row",
						}}
					>
						<Radio
							name="mode"
							value="teaching"
							label={t("overview.teachingMode")}
							className="ClassroomCreate_monitorRadio"
							onChange={handleModeChange}
							checked={!monitorModeChecked}
						/>

						<Radio
							name="mode"
							value="monitor"
							label={t("overview.monitorMode")}
							className="ClassroomCreate_monitorRadio"
							onChange={handleModeChange}
							checked={monitorModeChecked}
						/>
					</div>
					<InfoBox>{monitorModeChecked ? t("overview.monitorModeInfo") : t("overview.teachingModeInfo")}</InfoBox>
				</div>
			) : null}
			{
				<>
					<br />
					<div style={{ display: "flex", flexDirection: "row", alignItems: "start" }}>
						<Checkbox checked={pinned} onChecked={(isChecked) => setPinned(isChecked)} label="" />
						<div>
							<div className="ClassroomCreate_Pin_Container">
								<div className="ClassroomCreate_Pin">
									<IconPinClass />
								</div>
								<div>{t("overview.pinThisClass")}</div>
							</div>
							<div className="ClassroomCreate_Pin_Container_Text_Secondary">{t("overview.pinThisClassHelp")}</div>
						</div>
					</div>
				</>
			}
		</StandardModal>
	);
}

export default ClassroomCreate;
